import type { Api, Filter } from "../../../data";
import type { InquiryStage, Surcharge } from "@talentkontor/domain";

/**
 * Anfragen und Angebote (0072): Lader und Schreibwege. Vorstufen vor der Kooperation stehen in eigenen Tabellen und zählen
 * nirgends als Kooperation. Rechte prüft die Datenbank: Anfragen sieht das ganze Team, Creator nur, wer sie betreut, Angebote
 * und Preislisten nur mit „Beträge sehen“ (ohne Recht bleiben die Listen leer).
 */

export interface InquiryRow {
  id: string;
  org_id: string;
  brand_id: string;
  contact_id: string | null;
  title: string;
  source: string | null;
  received_on: string;
  manager_id: string | null;
  campaign_id: string | null;
  start_on: string | null;
  end_on: string | null;
  budget_cents: number | null;
  currency: string;
  platforms: string[];
  usage_months: number | null;
  exclusive: boolean;
  exclusive_until: string | null;
  exclusive_scope: string | null;
  rush: boolean;
  briefing: string | null;
  note: string | null;
  stage: InquiryStage;
  stage_changed_at: string;
  close_reason: string | null;
  close_note: string | null;
  closed_at: string | null;
  created_at: string;
  updated_at: string;
}
export type CandidateStatus = "proposed" | "accepted" | "declined" | "lost";
export interface CandidateRow {
  id: string;
  inquiry_id: string;
  talent_id: string;
  status: CandidateStatus;
  deal_id: string | null;
  reason: string | null;
  reason_note: string | null;
  decided_at: string | null;
  note: string | null;
  sort_order: number;
}
export type OfferStatus = "draft" | "final" | "sent" | "superseded";
export interface OfferRow {
  id: string;
  inquiry_id: string;
  version: number;
  status: OfferStatus;
  number: string | null;
  currency: string;
  language: "de" | "en";
  valid_until: string | null;
  prices_basis: "net" | "gross";
  commission_included: boolean;
  agency_fee_bp: number | null;
  intro: string | null;
  terms: string | null;
  subtotal_cents: number;
  fee_cents: number;
  total_cents: number;
  issuer_snapshot: Record<string, unknown> | null;
  recipient_snapshot: Record<string, unknown> | null;
  finalized_at: string | null;
  sent_at: string | null;
  sent_to: string | null;
  sent_external: boolean;
  pdf_path: string | null;
  created_at: string;
  updated_at: string;
}
export interface OfferLineRow {
  id: string;
  offer_id: string;
  candidate_id: string;
  platform: string;
  format: string;
  format_label: string | null;
  title: string | null;
  note: string | null;
  quantity: number;
  unit_price_cents: number;
  surcharges: Surcharge[];
  amount_cents: number;
  sort_order: number;
}
export interface EventRow {
  id: string;
  inquiry_id: string;
  event: string;
  actor: string | null;
  details: Record<string, unknown>;
  created_at: string;
}
export interface OfferSettings {
  org_id: string;
  number_template: string;
  number_prefix: string;
  valid_days: number;
  follow_up_days: number;
  prices_basis: "net" | "gross";
  commission_included: boolean;
  talent_sees_rate_card: boolean;
  decline_reasons: string[];
  default_intro: string | null;
  default_terms: string | null;
  updated_at: string;
}
export interface RateCardRow {
  id: string;
  org_id: string;
  talent_id: string;
  currency: string;
  usage_bp: number | null;
  usage_per_month: boolean;
  exclusivity_bp: number | null;
  exclusivity_per_month: boolean;
  rush_bp: number | null;
  revision_bp: number | null;
  note: string | null;
  updated_at: string;
}
export interface RateItemRow {
  id: string;
  card_id: string;
  platform: string;
  format: string;
  format_label: string | null;
  price_cents: number;
  note: string | null;
  sort_order: number;
}
export interface MemberLite {
  user_id: string;
  name: string;
}
export interface ContactLite {
  id: string;
  brand_id: string;
  name: string;
  email: string | null;
}

/** Ansprechperson der Anfrage; ohne gewählte (CO-019) der einzige Kontakt der Marke, sonst keine */
export function kontaktDerAnfrage(d: { inquiry: { contact_id: string | null }; contacts: readonly ContactLite[] }): ContactLite | undefined {
  if (d.inquiry.contact_id) return d.contacts.find((c) => c.id === d.inquiry.contact_id);
  return d.contacts.length === 1 ? d.contacts[0] : undefined;
}

const byOrg = (orgId: string): Filter[] => [["org_id", "eq", orgId]];
/** Nicht lesbar (fehlendes Recht) ergibt eine leere Liste */
function quiet<T>(p: Promise<T[]>): Promise<T[]> {
  return p.catch(() => [] as T[]);
}

const OFFER_LIST_COLUMNS = "id, inquiry_id, version, status, number, currency, total_cents, subtotal_cents, sent_at, valid_until, created_at";

export interface AnfragenDaten {
  inquiries: InquiryRow[];
  candidates: CandidateRow[];
  offers: Pick<OfferRow, "id" | "inquiry_id" | "version" | "status" | "number" | "currency" | "total_cents" | "subtotal_cents" | "sent_at" | "valid_until" | "created_at">[];
  members: MemberLite[];
  settings: OfferSettings | null;
}

/** Liste und Board: alle Anfragen, Vorschläge (sichtbare Creator), Angebote (mit Beträgen), Team für „Verantwortlich“ */
export async function ladeAnfragen(api: Api, orgId: string): Promise<AnfragenDaten> {
  const by = byOrg(orgId);
  const [inquiries, candidates, offers, members, settings] = await Promise.all([
    api.select<InquiryRow>("inquiries", { filters: by, order: [["received_on", "desc"], ["created_at", "desc"]] }),
    api.select<CandidateRow>("inquiry_candidates", { filters: by, order: [["sort_order", "asc"]] }),
    quiet(api.select<AnfragenDaten["offers"][number]>("inquiry_offers", { columns: OFFER_LIST_COLUMNS, filters: by, order: [["version", "asc"]] })),
    ladeTeam(api, orgId),
    ladeAngebotsEinstellungen(api, orgId),
  ]);
  return { inquiries, candidates, offers, members, settings };
}

export async function ladeTeam(api: Api, orgId: string): Promise<MemberLite[]> {
  const rows = await quiet(api.select<{ user_id: string; display_name: string | null; active: boolean }>("memberships", { columns: "user_id, display_name, active", filters: byOrg(orgId) }));
  return rows.filter((m) => m.active).map((m) => ({ user_id: m.user_id, name: m.display_name?.trim() || "Ohne Namen" }));
}

export async function ladeAngebotsEinstellungen(api: Api, orgId: string): Promise<OfferSettings | null> {
  return (await quiet(api.select<OfferSettings>("org_offer_settings", { filters: byOrg(orgId) })))[0] ?? null;
}

export interface AnfrageDetail {
  inquiry: InquiryRow;
  candidates: CandidateRow[];
  offers: OfferRow[];
  lines: OfferLineRow[];
  events: EventRow[];
  contacts: ContactLite[];
  cards: RateCardRow[];
  items: RateItemRow[];
  members: MemberLite[];
  settings: OfferSettings | null;
  /** Agentur für Entwürfe des PDF (festgeschriebene Angebote tragen die eingefrorenen Parteien) */
  org: Record<string, unknown> | null;
  legal: Record<string, unknown> | null;
  brand: Record<string, unknown> | null;
  /** Angebote mit Positionen von Creatorn, die die Person nicht betreut (RECHTE-R3-2): kein Senden, keine neue Fassung, kein PDF */
  verdeckt: string[];
  /** Kandidaten von Creatorn, die die Person nicht betreut (CO-006): Zahl und ob einer zugesagt ist */
  verdeckteCreator: { count: number; accepted: boolean };
}

export async function ladeAnfrage(api: Api, orgId: string, id: string): Promise<AnfrageDetail | null> {
  const inquiry = (await api.select<InquiryRow>("inquiries", { filters: [["id", "eq", id], ["org_id", "eq", orgId]] }))[0];
  if (!inquiry) return null;
  const [candidates, offers, events, contacts, members, settings, org, legal, brand] = await Promise.all([
    api.select<CandidateRow>("inquiry_candidates", { filters: [["inquiry_id", "eq", id]], order: [["sort_order", "asc"]] }),
    quiet(api.select<OfferRow>("inquiry_offers", { filters: [["inquiry_id", "eq", id]], order: [["version", "asc"]] })),
    quiet(api.select<EventRow>("inquiry_events", { filters: [["inquiry_id", "eq", id]], order: [["created_at", "asc"]] })),
    quiet(api.select<ContactLite>("brand_contacts", { columns: "id, brand_id, name, email", filters: [["brand_id", "eq", inquiry.brand_id]], order: [["name", "asc"]] })),
    ladeTeam(api, orgId),
    ladeAngebotsEinstellungen(api, orgId),
    quiet(api.select<Record<string, unknown>>("org_settings", { filters: byOrg(orgId) })).then((r) => r[0] ?? null),
    quiet(api.select<Record<string, unknown>>("org_legal", { filters: byOrg(orgId) })).then((r) => r[0] ?? null),
    quiet(api.select<Record<string, unknown>>("brands", { filters: [["id", "eq", inquiry.brand_id]] })).then((r) => r[0] ?? null),
  ]);
  const lines = offers.length
    ? await quiet(api.select<OfferLineRow>("inquiry_offer_lines", { filters: [["offer_id", "in", offers.map((o) => o.id)]], order: [["sort_order", "asc"]] }))
    : [];
  const talentIds = candidates.map((c) => c.talent_id);
  const cards = talentIds.length ? await quiet(api.select<RateCardRow>("talent_rate_cards", { filters: [["talent_id", "in", talentIds]] })) : [];
  const items = cards.length
    ? await quiet(api.select<RateItemRow>("talent_rate_items", { filters: [["card_id", "in", cards.map((c) => c.id)]], order: [["sort_order", "asc"], ["platform", "asc"]] }))
    : [];
  const verdeckt = offers.length ? await api.read<string[] | null>("inquiry_offers_hidden", { p_inquiry: id }).then((r) => r ?? []).catch(() => [] as string[]) : [];
  const verdeckteCreator = await api.read<{ count: number; accepted: boolean } | null>("inquiry_candidates_hidden", { p_inquiry: id })
    .then((r) => r ?? { count: 0, accepted: false }).catch(() => ({ count: 0, accepted: false }));
  return { inquiry, candidates, offers, lines, events, contacts, cards, items, members, settings, org, legal, brand, verdeckt, verdeckteCreator };
}

/** Preisliste eines Creators (Agentur) */
export async function ladePreisliste(api: Api, talentId: string): Promise<{ card: RateCardRow | null; items: RateItemRow[] }> {
  const card = (await quiet(api.select<RateCardRow>("talent_rate_cards", { filters: [["talent_id", "eq", talentId]] })))[0] ?? null;
  const items = card ? await api.select<RateItemRow>("talent_rate_items", { filters: [["card_id", "eq", card.id]], order: [["sort_order", "asc"], ["platform", "asc"]] }) : [];
  return { card, items };
}

// ── Schreibwege ────────────────────────────────────────────────────────────

export interface SpeicherAnfrage {
  inquiry_id: string;
  created: boolean;
  added: number;
  removed: number;
  kept: { talent_id: string; status: string }[];
  updated_at: string;
}
export function speichereAnfrage(api: Api, id: string | null, values: Record<string, unknown>, talents: string[], newBrand: string | null, expectedUpdatedAt?: string | null) {
  return api.rpc<SpeicherAnfrage>("inquiry_save", { p_inquiry: id, p_values: values, p_talents: talents, p_new_brand: newBrand, p_expected_updated_at: expectedUpdatedAt ?? null });
}
export interface AngebotsZeile {
  candidate_id: string;
  platform: string;
  format: string;
  format_label: string | null;
  title: string | null;
  note: string | null;
  quantity: number;
  unit_price_cents: number;
  surcharges: Surcharge[];
  sort_order?: number;
}
export function speichereAngebot(api: Api, offerId: string | null, inquiryId: string, values: Record<string, unknown>, lines: AngebotsZeile[] | null) {
  return api.rpc<{ offer_id: string; version: number; subtotal_cents: number; fee_cents: number; total_cents: number }>("offer_save", {
    p_offer: offerId, p_inquiry: inquiryId, p_values: values, p_lines: lines,
  });
}
export const kopiereAngebot = (api: Api, offerId: string) => api.rpc<string>("offer_copy", { p_offer: offerId });
export const schreibeAngebotFest = (api: Api, offerId: string) => api.rpc<{ offer_id: string; number: string; status: string }>("offer_finalize", { p_offer: offerId });
export const loescheEntwurf = (api: Api, offerId: string) => api.remove("inquiry_offers", [["id", "eq", offerId]]);
export function sendeAngebot(api: Api, offerId: string, a: { recipient: string | null; cc: string[]; message: string | null; path: string | null; size: number | null; external: boolean }) {
  return api.rpc<{ offer_id: string; number: string; outbox_id: string | null }>("offer_send", {
    p_offer: offerId, p_recipient: a.recipient, p_cc: a.cc, p_message: a.message, p_document_path: a.path, p_document_size: a.size, p_external: a.external,
  });
}
/** Nachtest CO-010: neue Empfänger-Adresse eines Angebots als Kontakt der Marke ablegen und, wenn noch keiner gewählt ist, an die Anfrage hängen */
export async function speichereAngebotsKontakt(api: Api, input: { orgId: string; brandId: string; inquiryId: string; email: string; anfrageOhneKontakt: boolean }): Promise<void> {
  const email = input.email.trim();
  const neu = await api.insert<{ id: string }>("brand_contacts", { org_id: input.orgId, brand_id: input.brandId, name: email, email });
  const id = neu[0]?.id;
  if (id && input.anfrageOhneKontakt) await api.update("inquiries", [["id", "eq", input.inquiryId]], { contact_id: id });
}
export async function ladeAngebotsPdfHoch(api: Api, orgId: string, inquiryId: string, file: { bytes: Uint8Array; filename: string }): Promise<{ path: string; size: number }> {
  const safe = file.filename.replace(/[^\w.-]+/g, "_");
  const path = `${orgId}/inquiry/${inquiryId}/${Date.now()}_${safe}`;
  await api.upload("documents", path, new Blob([file.bytes as BlobPart], { type: "application/pdf" }));
  return { path, size: file.bytes.length };
}
export const setzeStand = (api: Api, id: string, stage: "inquiry" | "offer_sent" | "negotiation") => api.rpc<void>("inquiry_set_stage", { p_inquiry: id, p_stage: stage });
export const entscheide = (api: Api, candidateId: string, status: "declined" | "lost" | "proposed", reason: string | null, note: string | null) =>
  api.rpc<void>("inquiry_candidate_decide", { p_candidate: candidateId, p_status: status, p_reason: reason, p_note: note });
export const schliesseAb = (api: Api, id: string, outcome: "declined" | "lost", reason: string, note: string | null) =>
  api.rpc<string>("inquiry_close", { p_inquiry: id, p_outcome: outcome, p_reason: reason, p_note: note });
export const oeffneWieder = (api: Api, id: string) => api.rpc<string>("inquiry_reopen", { p_inquiry: id });
export const sageZu = (api: Api, candidateId: string, values: Record<string, unknown>) =>
  api.rpc<{ deal_id: string; deliverables: number; budget_cents: number }>("inquiry_candidate_accept", { p_candidate: candidateId, p_values: values });
export const loescheAnfrage = (api: Api, id: string) => api.remove("inquiries", [["id", "eq", id]]);

/** Preisliste speichern: Karte anlegen oder ändern, Preise vollständig ersetzen (eine Transaktion, rate_card_save) */
export function speicherePreisliste(api: Api, talentId: string, card: Omit<RateCardRow, "id" | "org_id" | "talent_id" | "updated_at">,
  items: Pick<RateItemRow, "platform" | "format" | "format_label" | "price_cents" | "note">[]): Promise<string> {
  return api.rpc<string>("rate_card_save", { p_talent: talentId, p_card: card, p_items: items });
}
export const loeschePreisliste = (api: Api, cardId: string) => api.remove("talent_rate_cards", [["id", "eq", cardId]]);
